import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar } from '@mui/material'
import Typography from '../../../../components/common/Typography'
import Button from '../../../../components/common/Button'
import MiniHeader from '../../../../components/layout/MiniHeader'
import { FiSearch, FiMessageSquare, FiDownload } from 'react-icons/fi'
import { downloadFile } from '../../services/chatApi'
import {
  fetchChatLogUsers,
  loadChatConversation,
  exportChatLogs,
} from '../../../../state/redux/actions/chatActions'

const ChatLog = () => {
  const dispatch = useDispatch()

  const [selectedUsers, setSelectedUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [conversationLoadedLocal, setConversationLoadedLocal] = useState(false)
  const [dateFilter, setDateFilter] = useState('week')

  const {
    chatLogUsers = [],
    chatLogUsersLoading = false,
    chatLogUsersError = null,
    chatLogConversation = [],
    chatLogConversationLoading = false,
    chatLogConversationError = null,
    totalMessages = 0,
    gdprNotice = '',
  } = useSelector(state => state.chat || {})

  // fetch users on mount
  useEffect(() => {
    dispatch(fetchChatLogUsers({ page: 1, limit: 100, search: '' }))
  }, [dispatch])

  // debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      dispatch(fetchChatLogUsers({ page: 1, limit: 100, search: searchTerm }))
    }, 500)
    return () => clearTimeout(id)
  }, [searchTerm, dispatch])

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return chatLogUsers
    const q = searchTerm.toLowerCase()
    return chatLogUsers.filter(u => {
      const name = (u.full_name || u.name || '').toLowerCase()
      const email = (u.email || '').toLowerCase()
      return name.includes(q) || email.includes(q)
    })
  }, [chatLogUsers, searchTerm])

  const handleUserClick = user => {
    if (selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id))
    } else {
      if (selectedUsers.length < 2) {
        setSelectedUsers([...selectedUsers, user])
      }
    }
  }

  const handleLoadChat = async () => {
    if (selectedUsers.length < 2) return
    try {
      await dispatch(
        loadChatConversation({ userIds: selectedUsers.map(u => u.id), dateFilter })
      ).unwrap()
      setConversationLoadedLocal(true)
    } catch (err) {
      console.error('loadChatConversation failed', err)
      setConversationLoadedLocal(true) // keep behaviour similar to previous (shows dummy fallback if slice provided)
    }
  }

  const handleExportChat = async format => {
    if (selectedUsers.length < 2) return
    try {
      const payload = await dispatch(
        exportChatLogs({
          userIds: selectedUsers.map(u => u.id).join(','),
          format: format || 'csv',
          dateFilter,
        })
      ).unwrap()

      // chatActions returns { data, format }
      const data = payload?.data ?? payload
      const respFormat = payload?.format ?? format

      if (respFormat === 'csv' && data) {
        const filename = `chat-log-${selectedUsers.map(u => u.name).join('-')}-${new Date().toISOString().split('T')[0]}.csv`
        downloadFile(data, filename)
      } else if (respFormat === 'pdf' && data instanceof Blob) {
        const filename = `chat-log-${selectedUsers.map(u => u.name).join('-')}-${new Date().toISOString().split('T')[0]}.pdf`
        downloadFile(data, filename)
      } else {
        console.log('Export result:', data)
      }
    } catch (err) {
      console.error('exportChatLogs failed', err)
    }
  }

  // transform conversation messages for display
  const messages = useMemo(() => {
    return (chatLogConversation || []).map((msg, index) => {
      const timestamp = msg.timestamp || msg.createdAt || msg.time
      return {
        id: msg._id || msg.id || index,
        sender: msg.senderName || msg.sender?.name || msg.fromName || 'Unknown',
        senderId: msg.senderId || msg.sender?._id || msg.fromId || null,
        senderRole: msg.senderRole || msg.sender?.role,
        message: msg.message || msg.content || msg.text || '',
        timestamp: timestamp ? new Date(timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }) : 'Unknown time',
        read: msg.read,
      }
    })
  }, [chatLogConversation])

  const loadingUsers = chatLogUsersLoading
  const loading = chatLogConversationLoading

  return (
    <>
      <MiniHeader title="Chat Log Viewer" />
      <div className="ml-[250px] flex h-[calc(100vh-64px)] mt-6">
        {/* Left Sidebar */}
        <div className="w-[350px] p-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Selected Users Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Typography variant="h5" className="font-semibold text-gray-900">
                  Selected Users
                </Typography>
                <Typography variant="body" className="text-xs text-gray-500">
                  ({selectedUsers.length})
                </Typography>
              </div>
              {selectedUsers.length >= 2 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleLoadChat}
                  disabled={loading}
                  className="flex items-center gap-2 text-xs px-3 py-1.5"
                >
                  <FiMessageSquare className="text-sm" />
                  {loading ? 'Loading...' : 'Load Chat'}
                </Button>
              )}
            </div>
            {selectedUsers.length === 0 ? (
              <Typography variant="body" className="text-xs text-gray-400">
                Select at least 2 users to view their conversation
              </Typography>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <div
                    key={user.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#cdeded] rounded-md border border-teal-600"
                  >
                    <Typography className="text-xs text-gray-900">{user.name}</Typography>
                    <button onClick={() => handleUserClick(user)} className="text-gray-500 hover:text-gray-700 text-sm">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search Users Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <Typography variant="h5" className="font-semibold text-gray-900 mb-3">
              Search Users
            </Typography>
            <div className="relative mb-3">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-[#f3f3f5] border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {chatLogUsersError && !loadingUsers && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{chatLogUsersError}</div>
            )}

            {/* Users List */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {loadingUsers ? (
                <div className="text-center py-4">
                  <Typography className="text-xs text-gray-500">Loading users...</Typography>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-4">
                  <Typography className="text-xs text-gray-500">{searchTerm ? 'No users found matching your search' : 'No users available'}</Typography>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div
                    key={user._id || user.id}
                    onClick={() =>
                      handleUserClick({
                        id: user._id || user.id,
                        name: user.full_name || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                        email: user.email,
                        role: user.role === 'A' ? 'Agent' : user.role === 'C' ? 'Customer' : user.role || 'User',
                        initials: (user.full_name || user.name || user.firstName || 'U').charAt(0).toUpperCase(),
                        color: '#14B8A6',
                      })
                    }
                    className={`flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors border ${selectedUsers.find(u => u.id === (user._id || user.id)) ? 'bg-[#cdeded] border-teal-600' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#14B8A6',
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem',
                      }}
                    >
                      {(user.full_name || user.name || user.firstName || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <Typography className="text-sm font-medium text-gray-900 truncate">{user.full_name || user.name}</Typography>
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${ (user.role === 'A' || user.role === 'Agent') ? 'bg-teal-50 text-teal-700 border border-teal-600' : 'bg-gray-100 text-gray-700' }`}
                        >
                          {user.role === 'A' ? 'Agent' : user.role === 'C' ? 'Customer' : user.role || 'User'}
                        </span>
                      </div>
                      <Typography className="text-xs text-gray-500 truncate">{user.email}</Typography>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col p-4">
          {!conversationLoadedLocal ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col items-center justify-center p-8">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6">
                  <FiMessageSquare className="text-gray-400 text-6xl" />
                  <Typography variant="h3" className="text-lg font-semibold text-gray-900 absolute mt-24">
                    No Conversation Selected
                  </Typography>
                </div>
                <Typography className="text-sm text-gray-500 mt-6 text-center">
                  Select at least 2 users from the list and click &quot;Load Chat&quot; to view their conversation
                  history
                </Typography>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <Typography variant="h4" className="font-semibold text-gray-900 mb-1">
                  Conversation
                </Typography>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <span className="font-medium">Between:</span>
                  <span>{selectedUsers.map(u => u.name).join(' & ')}</span>
                </div>
                <Typography className="text-xs text-gray-500 mt-1">Total Messages: {totalMessages || messages.length}</Typography>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map(message => {
                  const isCurrentUser = message.senderId === selectedUsers[1]?.id
                  const user = selectedUsers.find(u => u.id === message.senderId)

                  return (
                    <div key={message.id} className={`flex gap-3 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                      {!isCurrentUser && (
                        <Avatar
                          sx={{
                            bgcolor: user?.color || '#14B8A6',
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem',
                          }}
                        >
                          {user?.initials || 'A'}
                        </Avatar>
                      )}

                      <div className={`max-w-[70%] ${isCurrentUser ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Typography className="text-xs font-medium text-gray-900">{message.sender}</Typography>
                          <Typography className="text-xs text-gray-500">{message.timestamp}</Typography>
                        </div>
                        <div
                          className={`px-4 py-2 rounded-lg ${isCurrentUser ? 'bg-[#b8e5e5] text-gray-900' : 'bg-gray-100 text-gray-900'}`}
                        >
                          <Typography className="text-sm">{message.message}</Typography>
                        </div>
                      </div>

                      {isCurrentUser && (
                        <Avatar
                          sx={{
                            bgcolor: user?.color || '#14B8A6',
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem',
                          }}
                        >
                          {user?.initials || 'S'}
                        </Avatar>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="p-4">
                <div className="flex items-start gap-2 p-3 bg-[#fff3cd] border border-amber-300 rounded-lg">
                  <Typography className="text-xs text-amber-900 leading-relaxed">
                    <span className="font-semibold">⚠️ GDPR Notice:</span> {gdprNotice || 'This conversation data is accessed for support and compliance purposes. All data access is logged and monitored.'}
                  </Typography>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ChatLog